// src/hooks/useLandingAttributionCache.ts
// Zwischenspeichert UTM/fbclid/gclid/Experiment-Parameter + Landingpage beim
// ERSTEN Seitenaufruf der App (nicht erst bei /register) in sessionStorage.
// Grund: der Schnell-Check (/check) ist mehrstufig -- navigiert der Nutzer
// von der Landingpage ueber /check nach /register, sind die urspruenglichen
// Query-Parameter aus der URL laengst weg (siehe PHASE-0-BEFUND.md, Abschnitt 2).
//
// Gleiches Muster wie pending_checkout_plan (sessionStorage, siehe Checkout.tsx/
// App.tsx). useRegistrationAttribution.ts liest diesen Cache zuerst (siehe dort);
// ohne Cache (heutiger Direktaufruf von /register) ist das Verhalten unveraendert.
//
// "Erster Seitenaufruf" = nur schreiben, wenn der Cache noch leer ist --
// spaetere interne Navigationen ueberschreiben nichts.
import { useEffect } from "react";

const CACHE_KEY = "pp_landing_attribution";

const WHITELIST_KEYS = [
  "fbclid",
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "lp_variant",
  "angle",
  "exp_id",
  "exp_variant",
  "aid",
] as const;

export type CachedLandingAttribution = Partial<Record<(typeof WHITELIST_KEYS)[number], string>> & {
  landingPath?: string;
};

/** Liest den Cache, falls vorhanden (fuer useRegistrationAttribution.ts). */
export function readLandingAttributionCache(): CachedLandingAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedLandingAttribution;
  } catch {
    return null;
  }
}

/** Einmal pro App-Mount in App.tsx aufrufen. */
export function useLandingAttributionCache(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (sessionStorage.getItem(CACHE_KEY)) return; // schon gesetzt -- erster Aufruf gewinnt

      const params = new URLSearchParams(window.location.search);
      const cache: CachedLandingAttribution = { landingPath: window.location.pathname };
      for (const key of WHITELIST_KEYS) {
        const value = params.get(key);
        if (value) cache[key] = value;
      }
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
      // sessionStorage nicht verfuegbar (privater Modus o. ae.) -- kein Crash, Attribution geht
      // in diesem Fall verloren, genau wie vorher ohne Cache.
    }
    // Bewusst [] -- nur beim allerersten Mount, liest window.location direkt (nicht useLocation,
    // der React-Router-State koennte durch spaetere Navigation schon veraendert sein).
  }, []);
}
